import styled from "styled-components";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";

interface StylesheetProps {
  platform: ScreenPlatform;
}

export const Container = styled.div<StylesheetProps>`
  padding: ${(props) => (props.platform === ScreenPlatform.MOBILE ? "0" : "90px")} 15px 24px;
  margin-top: ${(props) => (props.platform === ScreenPlatform.MOBILE ? "5rem" : "0")};
  height: 100%;
  overflow-y: auto;
`;

export const PageTitleSection = styled.div`
  margin-bottom: 16px;

  .page-subtitle {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    color: ${(props) => props.theme.colors.primary};
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  svg {
    font-size: 18px;
  }
`;

export const HelpButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: ${(props) => props.theme.colors.primary};
  border: 1px solid ${(props) => props.theme.colors.primary};
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    font-size: 18px;
  }
`;

export const GlossaryContainer = styled.div`
  width: 440px;
  max-width: 100vw;
  padding-bottom: 32px;

  @media (max-width: 600px) {
    width: 100vw;
  }

  .MuiAccordionSummary-content {
    align-items: center;
  }

  .MuiAccordionSummary-content .MuiTypography-root {
    font-size: 0.88rem;
    font-weight: 600;
    color: #333;
  }
`;

export const GlossaryHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;

  .glossary-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .glossary-title svg {
    font-size: 22px;
  }

  button {
    color: #fff;
  }
`;

export const GlossaryIntro = styled.div`
  padding: 16px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0 0 8px;
    font-size: 0.95rem;
    font-weight: 700;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.5;
    color: #555;
  }
`;

export const GlossaryEntryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.5;
    color: #444;
  }

  .entry-field {
    font-size: 0.82rem;
    color: #444;
    line-height: 1.45;
  }

  .entry-field strong {
    color: #333;
  }

  .meta-hint {
    display: block;
    margin-top: 6px;
    font-size: 0.78rem;
    font-style: italic;
    color: #777;
  }
`;

/** Title of a section of the glossary (cycle causes, one per group, telemetry alerts). */
/** Title of a part of the glossary (cycle causes, telemetry alerts). */
export const GlossarySectionTitle = styled.div`
  padding: 18px 20px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #002951;
`;

/**
 * One card per group of the glossary. Each card opens in place: no margin jump when it expands, and the
 * others stay as they are.
 */
export const GlossaryGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px;

  && .MuiAccordion-root {
    margin: 0;
    border-radius: 8px;
    border-left: 4px solid var(--gc, #002951);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.14);

    &:before {
      display: none;
    }
  }

  && .MuiAccordion-root.Mui-expanded {
    margin: 0;
  }

  && .MuiAccordionSummary-root,
  && .MuiAccordionSummary-root.Mui-expanded {
    min-height: 52px;
  }

  && .MuiAccordionSummary-content,
  && .MuiAccordionSummary-content.Mui-expanded {
    margin: 12px 0;
  }

  .group-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
  }

  .group-count {
    font-size: 0.72rem;
    font-weight: 500;
    color: #777;
    background: #f1f3f6;
    border-radius: 10px;
    padding: 2px 8px;
    white-space: nowrap;
  }

  .group-body {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .glossary-item {
    padding: 10px 0;
    border-top: 1px solid #eeeeee;

    h4 {
      margin: 0 0 4px;
      font-size: 0.85rem;
      font-weight: 600;
      color: #333;
    }

    p {
      margin: 0 0 4px;
      font-size: 0.8rem;
      line-height: 1.45;
      color: #555;
    }

    .todo {
      font-size: 0.78rem;
      color: #002951;
    }

    .meta {
      font-size: 0.75rem;
      color: #888;
    }
  }

  .glossary-note {
    margin-top: 6px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #f5f7fa;
    font-size: 0.78rem;
    line-height: 1.45;
    color: #555;
  }
`;

export const MetadataChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  .MuiChip-root {
    background: #eef2f7;
    color: #37474f;
    font-size: 0.72rem;
    height: 24px;
  }
`;

export const KpiGrid = styled.div<StylesheetProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.platform === ScreenPlatform.MOBILE ? "1fr" : "repeat(4, 1fr)"};
  gap: 16px;
  margin-bottom: 24px;
`;

interface KpiCardStyledProps {
  accentColor: string;
  clickable?: boolean;
}

export const KpiCard = styled.div<KpiCardStyledProps>`
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  border-left: 4px solid ${(props) => props.accentColor};
  overflow: hidden;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: transform 0.2s, box-shadow 0.2s;

  ${(props) => props.clickable && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}

  .kpi-bg-icon {
    position: absolute;
    right: -4px;
    bottom: -8px;
    opacity: 0.06;
    font-size: 5.5rem;
    color: ${(props) => props.accentColor};
    pointer-events: none;
    line-height: 1;
  }

  .kpi-content {
    display: flex;
    flex-direction: column;
    z-index: 1;
    width: 100%;
  }

  .kpi-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: ${(props) => props.accentColor};
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .kpi-value-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 2px;
  }

  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    color: #333;
    line-height: 1;
  }

  .kpi-secondary {
    font-size: 0.75rem;
    color: #999;
  }

  .kpi-meta-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: ${(props) => props.accentColor};
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  .kpi-progress-track {
    position: relative;
    width: 100%;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    margin: 8px 0 4px;
  }

  .kpi-progress-target {
    position: absolute;
    top: -3px;
    bottom: -3px;
    width: 2px;
    background: #333;
  }

  .kpi-progress-fill {
    height: 100%;
    border-radius: 3px;
    background: ${(props) => props.accentColor};
    transition: width 0.6s ease;
  }
`;

/** One section of the page, spaced like the old chart rows and clear of the fixed header when scrolled to. */
export const SectionBlock = styled.div`
  margin-bottom: 24px;
  scroll-margin-top: 80px;
`;

export const ChartContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;

  .chart-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
  }

  .chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: #999;
    font-size: 0.875rem;
  }

  .chart-hint {
    margin: -10px 0 16px;
    font-size: 0.8rem;
    color: #999;
  }

  .places-chart {
    position: relative;
    height: 340px;
  }

  .daily-chart {
    position: relative;
    height: 300px;
  }

  .chart-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin: -6px 0 10px;
    font-size: 0.78rem;
    color: #555;

    span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    i {
      display: inline-block;
      width: 18px;
      height: 0;
      border-top: 2px solid;
    }

    i.dashed {
      border-top-style: dashed;
    }

    i.dotted {
      border-top-style: dotted;
      border-top-width: 3px;
    }
  }
`;

/** Cells of the cycle tables (page and drill-down): captions, rate bars, pills and the 5 state chips. */
export const CycleTable = styled.div`
  /* NewDataTable pins its pagination to the bottom of the screen; inside a card or a dialog it follows the table */
  .fixed--bottom {
    position: relative !important;
    bottom: auto !important;
    margin: 0;
    width: 100%;
    padding: 4px 15px 0;
    background: #fff;
  }

  /* Inside the drill-down the list fills the dialog height instead of the screen-based default */
  &.fill .dataTables_scrollBody {
    max-height: calc(90vh - 285px) !important;
  }

  &.fill .dataTables_wrapper {
    margin: 0;
  }

  .details-button {
    border: 1px solid #c5d3e3;
    border-radius: 4px;
    background: #fff;
    padding: 3px 10px;
    font-size: 0.78rem;
    color: #2a5c9a;
    cursor: pointer;

    &:hover {
      background: #f2f6fb;
    }
  }

  .cause-pill.highlighted {
    outline: 1px solid #002951;
  }

  .cell-caption {
    display: block;
    font-size: 0.75rem;
    color: #999;
  }

  .place-name, .link-button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    color: #002951;
    cursor: pointer;
    text-align: left;
  }

  .place-name:hover, .link-button:hover {
    text-decoration: underline;
  }

  .rate-cell {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
  }

  .rate-bar {
    display: inline-block;
    width: 90px;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 3px;
    }
  }

  .few-travels {
    font-size: 0.7rem;
    color: #999;
    background: #eeeeee;
    border-radius: 3px;
    padding: 0 6px;
  }

  .tip-host {
    position: relative;
    display: inline-block;
    cursor: default;
  }

  .tip {
    display: none;
    position: absolute;
    z-index: 30;
    left: 50%;
    top: calc(100% + 10px);
    transform: translateX(-50%);
    min-width: 260px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    text-align: left;
    white-space: nowrap;

    &:before {
      content: "";
      position: absolute;
      top: -6px;
      left: 50%;
      margin-left: -6px;
      border: 6px solid transparent;
      border-top: 0;
      border-bottom-color: #fff;
    }
  }

  .tip-host:hover .tip {
    display: block;
  }

  .tip-title {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #333;
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid #e6e8ec;
  }

  .tip-row {
    display: grid;
    grid-template-columns: 10px 1fr auto auto;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
    font-size: 0.78rem;
    color: #555;

    i {
      width: 10px;
      height: 10px;
      border-radius: 2px;
    }

    b {
      font-weight: 600;
      color: #333;
    }

    span:last-child {
      color: #999;
    }
  }

  .loss-bar {
    display: inline-flex;
    width: 150px;
    height: 10px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
    }
  }

  .cause-pill, .kind-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;
    background: #f1f3f6;
    color: #555;

    i {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }
  }

  .state-chips {
    display: inline-flex;
    gap: 3px;

    span {
      font-size: 0.72rem;
      padding: 1px 6px;
      border-radius: 3px;
      white-space: nowrap;
    }

    .state-on {
      background: #e8f5e9;
      color: #2e7d32;
      border: 1px solid transparent;
    }

    .state-off {
      color: #b03030;
      border: 1px dashed #ff6c5c;
    }
  }
`;

/** Stacked bar of the complete cycle and the four failure groups, with its legend. */
export const ImpactBar = styled.div`
  margin-bottom: 16px;

  .impact-stack {
    display: flex;
    height: 30px;
    border-radius: 6px;
    overflow: hidden;
    background: #e0e0e0;

    span {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      font-size: 0.8rem;
      font-weight: 500;
      color: #fff;
    }
  }

  .impact-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 10px;
    font-size: 0.8rem;
    color: #555;

    span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    i {
      width: 10px;
      height: 10px;
      border-radius: 2px;
    }

    b {
      font-weight: 500;
      color: #333;
    }
  }
`;

/** One card per failure group, with its causes as clickable rows. */
export const ImpactCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;

  .impact-card {
    border: 1px solid #dddddd;
    border-top: 4px solid;
    border-radius: 8px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 10px;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    button {
      width: calc(100% + 12px);
      margin: 0 -6px;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 3px 10px;
      padding: 6px;
      border: 0;
      border-radius: 6px;
      background: none;
      text-align: left;
      cursor: pointer;
      font-size: 0.85rem;
      color: #555;

      &:hover {
        background: #f5f7fa;
      }

      &:hover .cause-name {
        color: #002951;
        text-decoration: underline;
      }
    }
  }

  .impact-card-name {
    font-weight: 600;
    font-size: 0.95rem;
    color: #333;
  }

  .impact-card-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #333;
  }

  .impact-card-desc {
    margin: 0;
    font-size: 0.8rem;
    color: #999;
  }

  .cause-bar {
    grid-column: 1 / -1;
    height: 5px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 3px;
    }
  }

  .cause-action {
    grid-column: 1 / -1;
    font-size: 0.75rem;
    color: #999;
  }
`;

/** Title of a card with toggle groups on the right. */
export const PlacesToggles = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px 16px;

  .toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .toggle-group {
    display: inline-flex;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    overflow: hidden;

    button {
      border: 0;
      background: #fff;
      padding: 6px 14px;
      font-size: 0.8rem;
      font-weight: 500;
      text-transform: uppercase;
      color: rgba(0, 0, 0, 0.54);
      cursor: pointer;
    }

    button + button {
      border-left: 1px solid rgba(0, 0, 0, 0.12);
    }

    button[aria-pressed="true"] {
      background: #002951;
      color: #fff;
    }
  }
`;

/** Title of the hardware card with the filter chips on the right. */
export const HardwareTabs = styled(PlacesToggles)`
  .hw-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    button {
      border: 1px solid rgba(0, 0, 0, 0.23);
      background: #fff;
      border-radius: 16px;
      padding: 4px 12px;
      font-size: 0.8rem;
      cursor: pointer;
      color: rgba(0, 0, 0, 0.87);
    }

    button[aria-pressed="true"] {
      background: #597aa7;
      border-color: #597aa7;
      color: #fff;
    }
  }
`;

/**
 * Body of the drill-down dialog: summary panel on the left, list on the right filling the height.
 */
export const DrilldownBody = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 34%) minmax(0, 1fr);
  gap: 24px;
  height: 100%;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  .drill-side {
    overflow-y: auto;
    padding-right: 4px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .drill-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .tile {
    border: 1px solid #e3e6ea;
    border-radius: 8px;
    padding: 10px 12px;
    background: #fafbfc;

    span {
      display: block;
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #999;
    }

    b {
      display: block;
      margin-top: 2px;
      font-size: 1.2rem;
      font-weight: 700;
      color: #333;
    }

    small {
      font-size: 0.75rem;
      font-weight: 400;
      color: #777;
    }

    &.wide {
      grid-column: span 2;
    }

    &.text b {
      font-size: 0.95rem;
    }
  }

  .label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #999;
    margin-bottom: 4px;
  }

  .text-block p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: #444;
  }

  .group-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #fff;
  }

  .bar-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .bar-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 3px 8px;
    font-size: 0.82rem;
    color: #444;

    b {
      font-weight: 600;
      color: #333;
    }

    .bar-track {
      grid-column: 1 / -1;
      height: 6px;
      background: #e0e0e0;
      border-radius: 3px;
      overflow: hidden;
    }

    .bar-track i {
      display: block;
      height: 100%;
      border-radius: 3px;
    }
  }

  .list-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #999;
  }

  .mini-toggle {
    display: inline-flex;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    overflow: hidden;

    button {
      border: 0;
      background: #fff;
      padding: 3px 10px;
      font-size: 0.72rem;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.54);
      cursor: pointer;
    }

    button + button {
      border-left: 1px solid rgba(0, 0, 0, 0.12);
    }

    button[aria-pressed="true"] {
      background: #002951;
      color: #fff;
    }
  }
`;

/** Header of the drill-down dialog: breadcrumb, title and summary. */
export const DrilldownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
    padding: 4px 10px;
    border: 1px solid #c5d3e3;
    border-radius: 4px;
    background: #fff;
    color: #2a5c9a;
    font-size: 0.8rem;
    cursor: pointer;

    &:hover {
      background: #f2f6fb;
    }
  }

  .drilldown-crumb {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 0.8rem;
    color: #999;

    button {
      border: 0;
      background: none;
      padding: 0;
      font: inherit;
      color: #597aa7;
      cursor: pointer;
    }
  }

  h2 {
    margin: 2px 0;
    font-size: 1.25rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #555;
  }

  .link-button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    color: #002951;
    cursor: pointer;
    text-decoration: underline;
  }
`;

/** Chip of the regional or plant clicked in the table, which filters the screen until removed. */
export const QuickFilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 4px 2px 10px;
  border-radius: 16px;
  background: #597aa7;
  color: #fff;
  font-size: 0.75rem;

  button {
    display: inline-flex;
    border: 0;
    background: none;
    padding: 0;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
  }
`;

/** Section title above the telemetry alerts. */
export const SectionTitle = styled.div`
  margin: 8px 0 12px;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }

  p {
    margin: 2px 0 0;
    font-size: 0.8rem;
    color: #999;
  }
`;

